import { ReactComponent as InstagramIcon } from '../static/icons/f-instagram.svg';
import { ReactComponent as TiktokIcon } from '../static/icons/f-tiktok.svg';
import { ReactComponent as FacebookIcon } from '../static/icons/f-facebook.svg';
import { ReactComponent as TwitterIcon } from '../static/icons/f-twitter.svg';
import { ReactComponent as YoutubeIcon } from '../static/icons/f-youtube.svg';
import { ReactComponent as InstagramColorIcon } from '../static/icons/f-instagram-colored.svg';
import { ReactComponent as TiktokColorIcon } from '../static/icons/f-tiktok-colored.svg';
import { ReactComponent as FacebookColorIcon } from '../static/icons/f-facebook-colored.svg';
import { ReactComponent as TwitterColorIcon } from '../static/icons/f-twitter-colored.svg';
import { ReactComponent as YoutubeColorIcon } from '../static/icons/f-youtube-colored.svg';

// Media analysis lifecycle. queued/processing are non-terminal (poll until done);
// completed/failed are terminal.
export const MEDIA_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

export const isMediaProcessing = (status) =>
  status === MEDIA_STATUS.QUEUED || status === MEDIA_STATUS.PROCESSING;

// Platform columns, in display order. `key` matches the backend
// `platform_performance` map key (X/Twitter is keyed `twitter`).
// `Icon` is the monochrome Flaticon brand SVG (used in the media table headers);
// `ColorIcon` is the full-color variant (used in the Platform Performance card).
// Both are SVGR components, rendered as <Icon /> / <ColorIcon />.
export const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', Icon: InstagramIcon, ColorIcon: InstagramColorIcon },
  { key: 'tiktok', label: 'TikTok', Icon: TiktokIcon, ColorIcon: TiktokColorIcon },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon, ColorIcon: FacebookColorIcon },
  { key: 'twitter', label: 'X', Icon: TwitterIcon, ColorIcon: TwitterColorIcon },
  { key: 'youtube', label: 'YouTube', Icon: YoutubeIcon, ColorIcon: YoutubeColorIcon },
];

// Some payloads key X by `twitter`, others by `x`.
const PLATFORM_KEY_ALIASES = { twitter: ['twitter', 'x'] };

// Look up a platform's entry in a `platform_performance` map, tolerating key
// aliases (e.g. twitter/x) and non-object input. Returns the entry or null.
export const getPlatformEntry = (platformPerformance, key) => {
  if (!platformPerformance || typeof platformPerformance !== 'object') return null;
  const aliases = PLATFORM_KEY_ALIASES[key] || [key];
  return aliases.map((alias) => platformPerformance[alias]).find(Boolean) || null;
};

// respData arrives as either a JSON string or an already-parsed object; return
// the object, or null when it's absent/malformed.
export const parseRespData = (respData) => {
  if (!respData) return null;
  if (typeof respData === 'object') return respData;
  try {
    return JSON.parse(respData);
  } catch {
    return null;
  }
};

// Backend rating → pill label + color level. The API abbreviates MEDIUM as
// `MED`, so both spellings map to the same pill.
export const RATING_MAP = {
  HIGH: { label: 'High', level: 'high' },
  MEDIUM: { label: 'Medium', level: 'medium' },
  MED: { label: 'Medium', level: 'medium' },
  LOW: { label: 'Bad', level: 'low' },
};

// Case-insensitive RATING_MAP lookup — some analyses (e.g. video) return
// title-case ratings ('Medium'/'Low') instead of the image path's uppercase.
export const getRatingInfo = (rating) => RATING_MAP[String(rating || '').toUpperCase()] || null;

// Viral Potential (coreAnalysis.viral_potential, shown as-is) → sort weight,
// used by the library sort and the Viral Potential table column.
export const VIRAL_ORDER = { High: 3, Moderate: 2, Low: 1 };

// coreAnalysis.performance_label → details-page score heading. The API sends
// either a tier enum (ELITE/STRONG/AVERAGE) or a display phrase ("Strong
// Performance Expected", "Average Performance", …). The filler words are
// stripped so every spelling resolves to its tier key below.
export const PERFORMANCE_HEADING_MAP = {
  ELITE: 'Strong Performance Expected',
  STRONG: 'Strong Performance Expected',
  AVERAGE: 'Average Performance Expected',
  'BELOW AVERAGE': 'Below Average Performance',
};

export const getPerformanceHeading = (performanceLabel) => {
  const key = String(performanceLabel || '')
    .toUpperCase()
    .replace(/\b(PERFORMANCE|EXPECTED)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return PERFORMANCE_HEADING_MAP[key] || 'Below Average Performance';
};

// Score → "…for selected audience" subtitle on the details score ring.
export const getScoreSubtitle = (score) => {
  if (score >= 70) return 'Above average for selected audience';
  if (score >= 40) return 'Average for selected audience';
  return 'Below average for selected audience';
};

// recommendation.impact (high|medium|low) → badge label + color level
// (high = green, medium = amber, low = red — see the pill styles in scss).
export const IMPACT_MAP = {
  high: { label: 'High Impact', level: 'high' },
  medium: { label: 'Medium Impact', level: 'medium' },
  low: { label: 'Low Impact', level: 'low' },
};

export const getImpact = (impact) =>
  IMPACT_MAP[String(impact || '').toLowerCase()] || IMPACT_MAP.medium;

// best_for enum → Best Use Case display label.
export const BEST_FOR_MAP = {
  ENGAGEMENT: 'Engagement',
  AWARENESS: 'Awareness',
  CONVERSION: 'Conversion',
};

// Funnel keys, in display order, for the details Engagement/Awareness/Conversion tabs.
export const FUNNEL_STAGES = [
  { key: 'engagement', label: 'Engagement' },
  { key: 'awareness', label: 'Awareness' },
  { key: 'conversion', label: 'Conversion' },
];

// Score ring arc color thresholds. Uses themes.scss color tokens; the arc stroke
// is applied as an inline style by antd Progress, so `var(...)` resolves. (The
// rail, below, is a raw SVG attribute and must stay a literal color.)
export const getScoreColor = (score) => {
  if (score >= 70) return 'var(--green-500)';
  if (score >= 40) return 'var(--yellow-300)';
  return 'var(--red-500)';
};

// Rail (unfilled track) color — a light tint of the matching stroke color, so
// the remaining portion of the ring stays visible. Must NOT equal getScoreColor,
// or the ring reads as 100% full.
export const getRailColor = (score) => {
  if (score >= 70) return '#DDEDE5'; // light green (matches rating-pill--high)
  if (score >= 40) return '#F6ECD2'; // light amber (matches rating-pill--medium)
  return '#F8E3E3'; // light red (matches rating-pill--low)
};

// Strip protocol (http/https) and everything after the domain (path, query,
// hash) so a URL displays as just its host, e.g.
// "https://www.example.com/foo?bar" → "www.example.com".
export const cleanUrl = (url) =>
  String(url || '')
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '');

// Extension-based guess for media URLs (thumbnailURL/imageURL) that may point
// at a video file — the API doesn't carry a reliable content-type for them.
const VIDEO_URL_RE = /\.(mp4|mov|m4v|webm|avi|wmv|mpe?g|mkv)(?:[?#]|$)/i;

export const isVideoUrl = (url) => VIDEO_URL_RE.test(String(url || ''));
